import { createContext, useState, useEffect } from "react";
import jwt_decode from "jwt-decode";
import { useHistory } from "react-router-dom";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  let [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null,
  );
  let [user, setUser] = useState(() =>
    localStorage.getItem("authTokens")
      ? jwt_decode(localStorage.getItem("authTokens"))
      : null,
  );
  let [loading, setLoading] = useState(true);

  // NEW STATE: Added login error state
  let [loginError, setLoginError] = useState("");

  // NEW STATE: Login Attempt Counter and Cool-down after Attempt
  let [loginAttempts, setLoginAttempts] = useState(0);
  let [cooldown, setCooldown] = useState(0);

  const history = useHistory();

  let loginUser = async (e) => {
    e.preventDefault();
    setLoginError("");

    // If cooldown active, block login
    if (cooldown > 0) {
      setLoginError(`Too many attempts. Please wait ${cooldown} seconds.`);
      return;
    }

    try {
      let response = await fetch("https://oss-lb.com:2265/api/token/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: e.target.username.value,
          password: e.target.password.value,
        }),
      });

      // Check if the response is JSON before parsing
      const contentType = response.headers.get("content-type");
      let data = {};
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      }

      if (response.status === 200) {
        setAuthTokens(data);
        setUser(jwt_decode(data.access));
        localStorage.setItem("authTokens", JSON.stringify(data));
        setLoginAttempts(0);
        setCooldown(0);
        history.push("/");
      } else {
        // ❌ Failed attempt
        setLoginAttempts((prev) => prev + 1);

        if (loginAttempts + 1 >= 5) {
          setLoginError("5 attempts made. Retry after 3 minutes.");
          setCooldown(180); // 180 seconds
        } else {
          setLoginError(data.detail || "Wrong username or password");
        }
      }
    } catch (error) {
      setLoginError("Network error. Please check your connection.");
    }
  };

  let logoutUser = () => {
    setAuthTokens(null);
    setUser(null);
    localStorage.removeItem("authTokens");
    setLoginError(""); // NEW: Clear error on logout
    history.push("/login");
  };

  let updateToken = async () => {
    if (authTokens) {
      let response = await fetch("https://oss-lb.com:2265/api/token/refresh/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: authTokens?.refresh }),
      });

      let data = await response.json();

      if (response.status === 200) {
        setAuthTokens(data);
        setUser(jwt_decode(data.access));
        localStorage.setItem("authTokens", JSON.stringify(data));
      } else {
        logoutUser();
      }
    } else {
      logoutUser();
    }
    if (loading) {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setLoginAttempts(0); // Reset attempts after cooldown
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  let contextData = {
    user: user,
    authTokens: authTokens,
    loginUser: loginUser,
    logoutUser: logoutUser,
    // NEW: Expose error state to components
    loginError: loginError,
    setLoginError: setLoginError,
    // NEW: Login attempt and cooldown expose to component
    loginAttempts,
    cooldown,
  };

  useEffect(() => {
    if (loading) {
      updateToken();
    }

    let fourMinutes = 1000 * 60 * 4;

    let interval = setInterval(() => {
      if (authTokens) {
        updateToken();
      }
    }, fourMinutes);
    return () => clearInterval(interval);
  }, [authTokens, loading]);

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
    </AuthContext.Provider>
  );
};
